import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { apiGet } from '../../utils/api';

const UserSearchInput = ({ onSelect, excludeIds = [] }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = async (q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await apiGet(`/leaderboards/search_users?q=${encodeURIComponent(q)}`);
      if (response.ok) {
        const data = await response.json();
        setResults((data.users || []).filter(u => !excludeIds.includes(u.public_id)));
        setShowDropdown(true);
      }
    } catch (err) {
      console.error('User search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (user) => {
    onSelect(user);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Search users by name or email..."
          className="w-full pl-9 pr-8 py-2 border bg-gray-50 text-gray-900 border-gray-300 rounded-lg text-sm font-rasa focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); setShowDropdown(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 font-rasa">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 font-rasa">
              {query.length < 2 ? 'Type at least 2 characters' : 'No users found'}
            </div>
          ) : (
            results.map(user => (
              <button
                key={user.public_id}
                onClick={() => handleSelect(user)}
                className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold flex-shrink-0">
                    {(user.full_name || user.username || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-700 font-rasa">{user.full_name || user.username}</p>
                  <p className="text-xs text-gray-400 font-rasa">{user.email}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchInput;
